import React, { useState, useEffect } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { InputOutput } from "../../InputOutput";
import Variables from "@/workflow_api/components/variables";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import {
  NodeTypes,
  type NodeData,
  type RefName,
} from "@/workflow_api/types/workflow";
import { useToast } from "@elaniintc/goes-design-system";
import {
  useUpdateNode,
  type UpdateNodeBody,
} from "@/workflow_api/services/nodes.api";
import { InteroperabilityConfigForm } from "./InteroperabilityConfigForm";
import type { InteroperabilityConfiguration } from "./types";
import { serializeInteroperabilityNode } from "./serialization";
import { deserializeInteroperabilityNode } from "./serialization/deserialization";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { useValidateService } from "@/queries/interoperability/interoperability.api";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

export const InteroperabilityDetailsForm: React.FC = () => {
  const { getNodeById, flowId } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const { variables: nodeVariables } = useGetVariables();
  const [title, setTitle] = useState<string>(
    nodeData.displayName || "Objeto Interoperabilidad",
  );
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );

  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);

  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });
  const [config, setConfig] = useState<InteroperabilityConfiguration | null>(
    null,
  );
  const [isConfigValid, setIsConfigValid] = useState(false);

  // Load existing configuration from node inputs
  useEffect(() => {
    if (nodeData) {
      const deserializedConfig = deserializeInteroperabilityNode(
        nodeData,
        nodeVariables,
      );
      if (deserializedConfig) {
        setConfig(deserializedConfig);
      }
    }
  }, [nodeData, nodeVariables]);

  return (
    <InteroperabilityModal
      title={title}
      setTitle={setTitle}
      modalState={modalState}
      setModalState={setModalState}
      flowId={flowId}
      nodeId={selectedNodeId}
      config={config}
      setConfig={setConfig}
      isConfigValid={isConfigValid}
      setIsConfigValid={setIsConfigValid}
      manualConfirmation={manualConfirmation}
      setManualConfirmation={setManualConfirmation}
      showNodeTitleValidation={showNodeTitleValidation}
      setShowNodeTitleValidation={setShowNodeTitleValidation}
      getNameValidationError={getNameValidationError}
      isNameValid={isNameValid}
    />
  );
};

const InteroperabilityModal: React.FC<{
  title: string;
  setTitle: (title: string) => void;
  modalState: "idle" | "close-warning";
  setModalState: (state: "idle" | "close-warning") => void;
  flowId: string;
  nodeId?: string;
  config: InteroperabilityConfiguration | null;
  setConfig: (config: InteroperabilityConfiguration | null) => void;
  isConfigValid: boolean;
  setIsConfigValid: (valid: boolean) => void;
  manualConfirmation: boolean;
  setManualConfirmation: (value: boolean) => void;
  showNodeTitleValidation: boolean;
  setShowNodeTitleValidation: (value: boolean) => void;
  getNameValidationError: (name: string) => string | undefined;
  isNameValid: (name: string) => boolean;
}> = ({
  title,
  setTitle,
  modalState,
  setModalState,
  flowId,
  nodeId,
  config,
  setConfig,
  isConfigValid,
  setIsConfigValid,
  manualConfirmation,
  setManualConfirmation,
  showNodeTitleValidation,
  setShowNodeTitleValidation,
  getNameValidationError,
  isNameValid,
}) => {
  const { nodeDetailsOpen, setNodeDetailsOpen, setSelectedNodeId } =
    useUIStore();
  const { getNodeById } = useWorkflowStore();
  const { mutateAsync: updateNode } = useUpdateNode(flowId);
  const { variables: nodeVariables } = useGetVariables();
  const { mutateAsync: validateService } = useValidateService();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = title.trim().length > 0 && isConfigValid;

  const saveNode = async (nodeTitle: string) => {
    if (!nodeId) {
      console.error("No node ID available");
      return false;
    }

    if (!config?.serviceId || !config?.endpointId) {
      toast.showToast({
        title: "Error de validación",
        description:
          "Debes seleccionar un servicio y un endpoint antes de guardar.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      return false;
    }

    const node = getNodeById(nodeId);
    if (!node) {
      console.error("Node not found");
      return false;
    }

    const nodeData = node.data as unknown as NodeData;

    try {
      // Validate service health before saving
      const validationResult = await validateService({
        serviceId: config.serviceId,
        endpointId: config.endpointId,
      });

      if (!validationResult.isAvailable) {
        toast.showToast({
          title: "Servicio no disponible",
          description:
            validationResult.errorMessage ||
            "El servicio seleccionado no está saludable. No se puede guardar el nodo.",
          options: {
            type: "error",
            duration: 5000,
          },
        });
        return false;
      }

      // Update config with validation result
      const validatedConfig: InteroperabilityConfiguration = {
        ...config,
        isServiceValidated: true,
        lastValidationDate: validationResult.lastChecked,
      };

      // Serialize configuration to inputs
      const inputs = validatedConfig
        ? serializeInteroperabilityNode(validatedConfig, nodeVariables)
        : [];

      const nodePayload: UpdateNodeBody = {
        display_name: nodeTitle,
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        order: nodeData.order,
        position_x: node.position.x,
        position_y: node.position.y,
        type: nodeData.node_type,
        manual_continue: manualConfirmation,
        inputs: inputs,
      };

      // Save node with inputs included
      await updateNode({
        node_id: nodeId,
        node: nodePayload,
      });
      return true;
    } catch (error: unknown) {
      console.error("Error saving interoperability node:", error);

      type ErrorWithResponse = {
        response?: {
          status?: number;
          data?: { message?: string };
        };
      };
      const err = (
        typeof error === "object" && error !== null
          ? (error as ErrorWithResponse)
          : {}
      ) as ErrorWithResponse;

      // Si el error es del healthcheck, mostramos el mensaje específico
      if (err.response?.status === 400 || err.response?.status === 500) {
        toast.showToast({
          title: "Error al validar servicio",
          description:
            err.response?.data?.message ||
            "No se pudo validar el servicio. Por favor, intenta nuevamente.",
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
      return false;
    }
  };

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={isFormValid && isNameValid(title)}
        isLoading={isSaving}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={async () => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          setIsSaving(true);
          const success = await saveNode(title);
          setIsSaving(false);
          if (success) {
            setNodeDetailsOpen(false);
            setSelectedNodeId();
          }
        }}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "diagram-nested",
            title: title,
            color: "warning",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                <Variables sourceNodeId={nodeId!} />
              </div>
            </>
          }
          leftContentWidth="min-w-[445px]"
          centerContent={
            <div>
              <InteroperabilityConfigForm
                initialConfig={config || undefined}
                onConfigChange={(newConfig) => {
                  setConfig(newConfig);
                }}
                onValidationChange={setIsConfigValid}
              />
            </div>
          }
          rightContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                <InputOutput
                  nodeType={NodeTypes.INTEROPERABILITY}
                  showSteps={false}
                  nodeLabel={title}
                  variableName=""
                  variableType="text"
                  params={{
                    serviceId: config?.serviceId,
                    endpointId: config?.endpointId,
                  }}
                />
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};
